import * as Config from '../../../config';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import { debounce } from "throttle-debounce";
import { Root, Trigger, Content, Item, Portal } from "@radix-ui/react-context-menu";
import '../common/Search.css';

import { setStateShortcuts } from "../shortcut/redux/actions/index";
import { getContextMenuPortalContainer, openContextMenuAt } from "../../../utils/zoomMenu";

import useTooltip from '../tooltip/hooks/useTooltip';
import useKeyPress from '../../../hooks/useKeyPress';

import ChevronActive from '../svg/ChevronActive';
import ChevronInactive from '../svg/ChevronInactive';
import IconSearch from './svg/IconSearch';
import IconClear from './svg/IconClear';
import IconSelected from './svg/IconSelected';

const TYPES = ['Artists', 'Tracks', 'Labels', 'Playlists'];

// TODO - LOCALHOST TEST
let lockDebounce = false;
function ConnectedSearchAdvanced({
  placeholder, className, style, onSuggestChange,
  api, storeRoute, setStateShortcuts
}) {

  const [value, setValue] = useState('');
  const [type, setType] = useState(`Artists`);
  const [lock, setLock] = useState(false);            // type menu open (driven by Radix onOpenChange)
  const [focus, setFocus] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [samples, setSamples] = useState([]);
  const prepareFocus = () => {
    setFocus(true);
    setStateShortcuts(false);
  }
  const prepareBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget) && !lock) {
      setFocus(false);
      setStateShortcuts(true);
      setSuggestOpen(false);
    }
  }
  const elInput = useRef();
  const onEnterPress = (e) => {
    if (e.charCode === 13 && value) {
      lockDebounce = true;
      setSuggestOpen(false);
      searchAction(value);

      elInput.current.blur();
    }
  }
  const searchAction = (value) => {
    const link = `/search/${type.toLowerCase()}/${value ? encodeURIComponent(value.replaceAll(` `, `+`)) : ``}`;
    (storeRoute && storeRoute.goTo) && storeRoute.goTo(link);
  }
  const prepareSearchAction = (value) => () => {
    setSuggestOpen(false);
    setValue(value);
    searchAction(value);
  }

  const debouncedApply = useCallback(debounce(1000, (v) => {
    const searchType = (type.toLowerCase() === 'playlists') ? `charts` : type.toLowerCase();
    // DEPRECATED - OLD SEARCH
    // api.getFromApi(`/v4/catalog/search/?type=${searchType}&q=${v}&per_page=5`, false).then( response => {
    //   if (lockDebounce) return;
    //   setSamples(response.data[searchType]);
    //   setSuggestOpen(response.data[searchType].length > 0);
    // } ).catch( e => console.log(`error`, e) );
    const formatSuggestionSearch = (item) => {
      const varSuggestion = `${searchType.slice(0, -1)}_name`;
      item.name = item.name ? item.name : ( (item[varSuggestion] ? item[varSuggestion] : '') )
      return item;
    }
    api.getFromApiSearch(`/${searchType}?count=5&q=${v}`, false, false).then( response => {
      if (lockDebounce) return;
      setSamples(response.data.data.map(formatSuggestionSearch));
      setSuggestOpen(response.data.data.length > 0);
    } ).catch( e => console.log(`error`, e) );

  }), [type])
  const onChange = (e) => {
    lockDebounce = false;
    setValue(e.target.value);
    if (e.target.value.length > 0) {
      debouncedApply(e.target.value);
    } else {
      setSuggestOpen(false);
    }
  }

  const clear = () => {
    setValue(``);
    setSuggestOpen(false);
    elInput.current.focus();
  }
  const clickType = (t) => () => {
    setType(t);
    elInput.current.focus();
  };
  const showContextMenu = (e) => {
    lockDebounce = true;
    setSuggestOpen(false);
    const p = e.currentTarget.getBoundingClientRect();
    openContextMenuAt(e.currentTarget, p.left + 102, p.top + p.height); // position V1
  }

  const showSuggest = suggestOpen && samples.length > 0;
  useEffect(() => {
    onSuggestChange && onSuggestChange(showSuggest);
  }, [showSuggest, onSuggestChange])

  const keyControlF = useKeyPress('f', true, true, true);
  useEffect(() => {
    if (keyControlF) {
      setTimeout(() => { elInput.current.focus() }, 100);
    }
  }, [keyControlF, elInput])

  useTooltip(`ref-search`, `Search`, `Search by artist, label, track or playlist`);

  const forceFocus = () => {
    if (Config.TOUCH_ENABLED) {
      elInput.current.focus()
    }
  }
  const onClickSearch = () => { onEnterPress({charCode: 13}) }

  return (
    <div id={`ref-search`} tabIndex={0} className={`${className} tab-bar-search search-box ${focus ? `focus` : `nofocus`} allow-menu`} style={style} onFocus={prepareFocus} onBlur={prepareBlur}>
      <IconSearch className={`icon-search`} onClick={onClickSearch} />

      {/* TYPE SELECTOR — Radix ContextMenu (ancrage curseur, comme FilterDate) */}
      <Root onOpenChange={setLock} modal={false}>
        <Trigger asChild>
          <div className={`search-select ${lock && 'active'}`} onClick={showContextMenu}>
            <div className={`flexRow`}>
              <span className={`grey-1`}>{type}</span>
              { !lock ? <ChevronInactive className={`chevron-search`} /> : <ChevronActive className={`chevron-search`} />  }
            </div>
          </div>
        </Trigger>
        <Portal container={getContextMenuPortalContainer()}>
          <Content
            className={'dynamic-menu-v2 search-compact'}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
            style={(window.window_zoom || 1) !== 1
              ? { transform: `scale(${window.window_zoom})`, transformOrigin: 'top left' }
              : undefined}
          >
            { TYPES.map((t) => (
              <Item key={t} onSelect={clickType(t)}>
                <div className={'flexRow space-between block-contextmenu-item-v2 all-hover'}>
                  {t}
                  {type === t ? <IconSelected size={18} /> : <IconSearch size={16} className={'hidden'} />}
                </div>
              </Item>
            )) }
          </Content>
        </Portal>
      </Root>

      <div className="search-clear" onClick={clear}>
        { value.length > 0 ? <IconClear size={14} className={`icon-clear`} /> : null }
      </div>
      <input ref={elInput} className={`search-input ${focus ? `focus` : `nofocus`} type-regular`}
        onKeyPress={onEnterPress} name={`search-advanced`} autoComplete={`off`}
        value={value} onChange={onChange} type="text" placeholder={focus ? '' : placeholder}
        onClick={forceFocus}
      />

      {/* SUGGESTIONS — dropdown CSS pur (scale nativement sous body{zoom}) */}
      { showSuggest && (
        <div className={'dynamic-menu-v2 search-recommand search-suggest'}>
          { samples.map((x, i) => (
            <div key={`s-${i}`}
              className={'flexRow block-contextmenu-item-v2 all-hover'}
              onMouseDown={e => e.preventDefault()}
              onClick={prepareSearchAction(x.name)}>
              <IconSearch size={16} />
              {x.name}
            </div>
          )) }
        </div>
      )}
    </div>
  )
}

ConnectedSearchAdvanced.propTypes = {
  placeholder: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  onSuggestChange: PropTypes.func
}

const SearchAdvanced = connect(
  state => ({
    api: state.api,
    storeRoute: state.storeRoute
  }),
  dispatch => ({
    setStateShortcuts: state => dispatch(setStateShortcuts(state))
  })
)(ConnectedSearchAdvanced);

export default SearchAdvanced;
